import {
  SHEETS_UI_ENGINE_PANEL_SURFACES,
  SHEETS_UI_ENGINE_PANELS,
  SHEETS_UI_FIELDS,
  SHEETS_UI_SURFACES,
  SHEETS_ZOOM,
  type SheetsUiCommand,
  type SheetsUiInspector,
  type SheetsUiItemTab,
  type SheetsUiSheetRef,
  type SheetsUiSurface,
} from '@sharedlib/common/sheets/ui-commands';

import { type SessionContext } from '@sharedlib/sheets-editor/editor-session';
import { type EngineUi } from '@sharedlib/sheets-editor/engine-ui';
import { type ItemMenuUi } from './item-menu-ui';
import { type ToolbarControl } from '@sharedlib/sheets-editor/toolbar-spec';
import { type UiInspector } from '@sharedlib/sheets-editor/ui-inspector';
import { NEEDS_THE_PERSON, downloadOptions, fontOptions, menuOptions, toolbarControl, type UiOption } from './ui-registry';

/**
 * The editor's side of the MCP contract's UI tools (6 Oct: `sheets_ui`, `sheets_view`, `sheets_selection`): one
 * command, checked by sheets-app already, run on the registered surface or control it names, and answered with
 * what the editor shows afterwards. A step the editor can't take is a failure with its reason, never a success
 * ("Do not silently pretend to click or open a panel").
 *
 * The editor lends what it owns through `UiHost`; the grid's controls are `EngineUi`. Kept free of Angular and the
 * engine, so each command is tested against a fake host.
 */

export interface UiHost {
  /** The grid's controls, once the engine is mounted. */
  grid(): EngineUi | null;
  /** What the user is looking at (§6.1's context). */
  context(): SessionContext | null;
  isOpen(surface: SheetsUiSurface): boolean;
  /** Opens the surface as its own button does. */
  open(surface: SheetsUiSurface): string | null | Promise<string | null>;
  /** Closes it as its X, Escape or a click elsewhere does. */
  close(surface: SheetsUiSurface): string | null;
  /** An open inspector's fields and buttons; null when it isn't open. */
  inspector(surface: SheetsUiInspector): UiInspector | null;
  /** Whether an open surface is still at work (a PDF being made, a file imported): it stays open then. */
  busy(surface: SheetsUiSurface): boolean;
  /** What the editor last told the person (its toast), the reason a step it refused gives. */
  lastMessage(): string | null;
  /** Whether the toolbar control is disabled (Undo with nothing to undo). */
  isDisabled(control: ToolbarControl): boolean;
  /** Clicks the control's main part, or double-clicks it. Resolves to why it didn't run, or null. */
  press(control: ToolbarControl, double: boolean): Promise<string | null>;
  /** Whether an option shows checked, and whether it is disabled. */
  optionState(option: UiOption): { readonly checked: boolean; readonly disabled: boolean };
  /** Clicks the option. */
  choose(option: UiOption): Promise<string | null>;
  /** Keyboard focus to the toolbar's first control (NFR-7). */
  focusToolbar(): string | null;
  shareable(): boolean;
  fontFamilies(): readonly string[];
  switchItemTab(tab: SheetsUiItemTab): string | null;
  openCompanion(mode: 'modal' | 'dynamic_panel'): Promise<string | null>;
  openSettings(): string | null;
  openWorkbook(workbookId: string, sheet?: SheetsUiSheetRef): string | null;
  closeWorkbook(): string | null;
  /** The item menu's entries, while it is open. */
  itemMenu(): ItemMenuUi | null;
  /** The rest of what shows: the save state, a banner, a dialog only the person can answer, the toast. */
  extraState(): Readonly<Record<string, unknown>>;
  /** Lets the page draw what the step changed before the state is read. */
  settle(): Promise<void>;
}

export type UiOutcome = { readonly ok: true; readonly state: Record<string, unknown> } | { readonly ok: false; readonly error: string };

const LOADING = 'the editor is still loading';

/** The options a surface offers; empty for one that has none. */
export function optionsFor(host: UiHost, surface: SheetsUiSurface): readonly UiOption[] {
  if (surface === 'menu.download') return downloadOptions(host.shareable());
  // Its entries are the menu's own (item-menu-ui.ts), listed with their submenu and focus state.
  if (surface === 'menu.item_actions') return [];
  if (surface === 'select.font') return fontOptions(host.fontFamilies());
  if (surface.startsWith('menu.')) return menuOptions(surface.slice('menu.'.length));
  return [];
}

const isInspector = (surface: string): surface is SheetsUiInspector => surface in SHEETS_UI_FIELDS;

/** What the editor shows: the answer to every command, and `get_state` itself. */
export function uiState(host: UiHost): Record<string, unknown> {
  const context = host.context();
  const grid = host.grid();
  const open = SHEETS_UI_SURFACES.filter((s) => host.isOpen(s));
  const menus: Record<string, unknown> = {};
  const inspectors: Record<string, unknown> = {};
  for (const surface of open) {
    if (surface === 'menu.item_actions') {
      // The parent and submenu expanded state of each entry, and which has keyboard focus (MCP contract, 6 Oct).
      const entries = host.itemMenu()?.entries() ?? [];
      menus[surface] = { options: entries, focused: entries.find((e) => e.focused)?.id ?? null };
      continue;
    }
    const options = optionsFor(host, surface);
    if (options.length) {
      menus[surface] = {
        options: options.map((o) => {
          const { checked, disabled } = host.optionState(o);
          return { id: o.id, label: o.label, ...(checked ? { checked } : {}), ...(disabled ? { disabled } : {}) };
        }),
      };
    }
    if (isInspector(surface)) {
      const inspector = host.inspector(surface);
      inspectors[surface] = { fields: inspector?.uiFields() ?? {}, ...inspector?.uiDetail?.(), ...(host.busy(surface) ? { busy: true } : {}) };
    }
  }
  return {
    ...(context
      ? {
          activeSheet: context.activeSheet,
          selection: { sheet: context.activeSheet, ranges: context.selections, activeCell: context.activeCell },
          mode: context.mode,
          topLeftCell: context.topLeftCell,
        }
      : {}),
    openSurfaces: open,
    menus,
    inspectors,
    formulaBar: { expanded: grid?.formulaBarExpanded() ?? null },
    zoom: { percent: grid?.zoomPercent() ?? null, min: SHEETS_ZOOM.min, max: SHEETS_ZOOM.max, step: SHEETS_ZOOM.step, reset: SHEETS_ZOOM.reset, presets: SHEETS_ZOOM.presets },
    notesShown: grid?.shownNotes() ?? [],
    // The floating images selected, as a click selects one (sheets_selection "select_image").
    images: { selected: grid?.selectedImages() ?? [] },
    // Paint format's state: "once" pastes on the next selection, "kept_on" on each until it is pressed again.
    formatPainter: grid?.formatPainter() ?? null,
    ...(grid?.lastLink() ? { lastLink: grid.lastLink() } : {}),
    ...host.extraState(),
  };
}

/** One command, run; then what shows. */
export async function runUiCommand(host: UiHost, command: SheetsUiCommand): Promise<UiOutcome> {
  let error: string | null;
  try {
    error = await step(host, command);
  } catch (thrown) {
    error = thrown instanceof Error ? thrown.message : 'the editor could not do that';
  }
  if (error) return { ok: false, error };
  await host.settle();
  return { ok: true, state: uiState(host) };
}

async function step(host: UiHost, command: SheetsUiCommand): Promise<string | null> {
  const grid = host.grid();
  switch (command.family) {
    case 'view':
      if (command.action === 'get') return null;
      if (!grid) return LOADING;
      switch (command.action) {
        case 'set_zoom':
          return grid.setZoom(command.percent);
        case 'zoom_in':
        case 'zoom_out': {
          // The footer's − and + buttons: a step of 10, within its range.
          const now = grid.zoomPercent() ?? SHEETS_ZOOM.reset;
          const next = now + (command.action === 'zoom_in' ? SHEETS_ZOOM.step : -SHEETS_ZOOM.step);
          if (next < SHEETS_ZOOM.min || next > SHEETS_ZOOM.max) return `zoom is at its ${command.action === 'zoom_in' ? 'largest' : 'smallest'} (${now}%)`;
          return grid.setZoom(next);
        }
        case 'reset_zoom':
          return grid.setZoom(SHEETS_ZOOM.reset);
        case 'show_note':
        case 'hide_note':
          return grid.setNoteShown(command.sheet, command.cell, command.action === 'show_note');
      }
      break;
    case 'selection':
      if (command.action === 'get') return null;
      if (!grid) return LOADING;
      switch (command.action) {
        case 'select':
          return grid.select(command.sheet, command.ranges, command.activeCell);
        case 'select_name':
          return grid.selectName(command.name);
        case 'select_all':
          return grid.selectAll(command.sheet);
        case 'select_image':
          return grid.selectImage(command.sheet, command.image);
        case 'reveal': {
          const cell = host.context()?.activeCell;
          return cell ? grid.reveal(cell) : 'no sheet is showing';
        }
      }
      break;
    case 'ui':
      return uiStep(host, grid, command);
  }
  return null;
}

async function uiStep(host: UiHost, grid: EngineUi | null, command: Extract<SheetsUiCommand, { family: 'ui' }>): Promise<string | null> {
  switch (command.action) {
    case 'get_state':
      return null;
    case 'open_surface':
      return host.isOpen(command.surface) ? null : host.open(command.surface);
    case 'close_surface':
    case 'cancel': {
      if (!host.isOpen(command.surface)) return command.action === 'cancel' ? `${command.surface} is not open` : null;
      const inspector = isInspector(command.surface) ? host.inspector(command.surface) : null;
      if (inspector) inspector.uiCancel();
      else {
        const refused = host.close(command.surface);
        if (refused) return refused;
      }
      await host.settle();
      return host.isOpen(command.surface) ? (host.busy(command.surface) ? `${command.surface} is busy and stays open` : `${command.surface} stayed open`) : null;
    }
    case 'set_field': {
      const inspector = host.inspector(command.surface);
      return inspector ? inspector.uiSet(command.field, command.value) : `${command.surface} is not open`;
    }
    case 'apply': {
      const inspector = host.inspector(command.surface);
      if (!inspector) return `${command.surface} is not open`;
      const refused = await inspector.uiApply();
      if (refused) return refused;
      await host.settle();
      // Done closes it; one still open and not at work refused what it was given, and its toast says why. A panel
      // whose list shows what was done stays open (the engine's defined names and protection, All functions).
      if (!inspector.uiApplyKeepsOpen && host.isOpen(command.surface) && !host.busy(command.surface)) return host.lastMessage() ?? `${command.surface} did not apply`;
      return null;
    }
    case 'remove': {
      const inspector = host.inspector(command.surface);
      if (!inspector?.uiRemove) return `${command.surface} is not open`;
      return inspector.uiRemove();
    }
    case 'press': {
      const control = toolbarControl(command.control);
      if (!control) return `there is no toolbar control ${command.control}`;
      if (control.kind === 'select') return `${control.id} is a select: choose with select_option on select.font`;
      if (command.double && !control.dblclick) return `${control.id} takes no double-click`;
      if (host.isDisabled(control)) return `${control.id} is disabled`;
      // A menu with no main part opens its menu, as clicking it does.
      if (!control.main && !command.double) return host.isOpen(`menu.${control.id}` as SheetsUiSurface) ? null : host.open(`menu.${control.id}` as SheetsUiSurface);
      return host.press(control, Boolean(command.double));
    }
    case 'select_option': {
      if (command.surface === 'menu.item_actions') {
        const menu = await openItemMenu(host);
        return typeof menu === 'string' ? menu : menu.choose(command.option);
      }
      const options = optionsFor(host, command.surface);
      const option = options.find((o) => o.id === command.option);
      if (!option) return `${command.surface} has no option ${command.option}; it has ${options.map((o) => o.id).join(', ')}`;
      const person = NEEDS_THE_PERSON[`${command.surface}/${command.option}`];
      if (person) return person;
      // An entry that opens one of the engine's own panels opens it as open_surface does (engine-panels.ts).
      const panel = SHEETS_UI_ENGINE_PANEL_SURFACES.find((p) => SHEETS_UI_ENGINE_PANELS[p].menu === command.surface && SHEETS_UI_ENGINE_PANELS[p].option === command.option);
      if (panel && !host.optionState(option).disabled) return host.isOpen(panel) ? null : host.open(panel);
      if (host.optionState(option).disabled) return `${option.label} is disabled`;
      return host.choose(option);
    }
    case 'begin_edit':
      return grid ? grid.beginEdit(command.cell) : LOADING;
    case 'commit_edit':
    case 'cancel_edit':
      return grid ? grid.endEdit(command.action === 'commit_edit') : LOADING;
    case 'expand_formula_bar':
    case 'collapse_formula_bar':
      return grid ? grid.setFormulaBar(command.action === 'expand_formula_bar') : LOADING;
    case 'focus':
      if (command.target === 'toolbar') return host.focusToolbar();
      return grid ? grid.focusGrid() : LOADING;
    case 'switch_item_tab':
      return host.switchItemTab(command.tab);
    case 'open_companion':
      return host.openCompanion(command.mode);
    case 'open_settings':
      return host.openSettings();
    case 'open_workbook':
      return host.openWorkbook(command.workbookId, command.sheet);
    case 'close_workbook':
      return host.closeWorkbook();
    case 'open_link':
      return grid ? grid.openLink(command.sheet, command.cell, command.link) : LOADING;
    case 'expand_submenu':
    case 'collapse_submenu': {
      const menu = await openItemMenu(host);
      if (typeof menu === 'string') return menu;
      const expanded = command.action === 'expand_submenu';
      const refused = menu.setExpanded(command.option, expanded);
      if (refused) return refused;
      await host.settle();
      return host.itemMenu()?.entries().find((e) => e.id === command.option)?.expanded === expanded
        ? null
        : `${command.option}'s submenu did not ${expanded ? 'open' : 'close'}`;
    }
  }
}

/** The item menu, opened first as a click on "…" opens it when it isn't. */
async function openItemMenu(host: UiHost): Promise<ItemMenuUi | string> {
  if (!host.isOpen('menu.item_actions')) {
    const refused = await host.open('menu.item_actions');
    if (refused) return refused;
    await host.settle();
  }
  return host.itemMenu() ?? 'the item menu did not open';
}
